import React, { useState } from "react"
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io"

import {
  showErrorToast,
  showInfoToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import moment from "moment"

type DateTimeState = {
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  activeField: "startDate" | "startTime" | "endDate" | "endTime"
}

interface CalendarProps {
  selectedDate: moment.Moment
  onChange: (
    date: moment.Moment,
    additionalUpdates?: Partial<DateTimeState>,
  ) => void
  minDate?: moment.Moment
  dateTimeState: DateTimeState
  allowSelectingPastDates?: boolean
}

const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onChange,
  minDate = moment().startOf("day"),
  dateTimeState,
  allowSelectingPastDates = false,
}) => {
  const [currentMonth, setCurrentMonth] = useState<moment.Moment>(
    moment(selectedDate),
  )

  // Function to render calendar header with month navigation
  const renderHeader = () => {
    const dateFormat = "MMMM YYYY"

    return (
      <div className="flex justify-between items-center mb-4">
        <span className="text-white/80 font-medium">
          {currentMonth.format(dateFormat)}{" "}
        </span>
        <div className="flex gap-4">
          <button
            onClick={prevMonth}
            className="text-[#0A84FF] p-1 bg-gray-400/10 "
            aria-label="Previous Month"
          >
            <IoIosArrowBack size={20} />
          </button>
          <button
            onClick={nextMonth}
            className="text-[#0A84FF] p-1 bg-gray-400/10"
            aria-label="Next Month"
          >
            <IoIosArrowForward size={20} />
          </button>
        </div>
      </div>
    )
  }

  // Function to render the days of the week
  const renderDays = () => {
    const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"]

    return (
      <div className="grid grid-cols-7 mb-2">
        {days.map((day) => (
          <div
            key={day}
            className="text-white/50 text-xs text-center font-medium py-1"
          >
            {day}
          </div>
        ))}
      </div>
    )
  }

  // Function to render the cells/dates of the calendar
  const renderCells = () => {
    const monthStart = moment(currentMonth).startOf("month")
    const monthEnd = moment(currentMonth).endOf("month")
    const startDate = moment(monthStart).startOf("week")
    const endDate = moment(monthEnd).endOf("week")

    const rows = []
    let days = []
    let day = startDate

    while (day.isSameOrBefore(endDate)) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = moment(day)
        const formattedDate = day.date()
        const isSelected = day.isSame(selectedDate, "day")
        const isCurrentMonth = day.isSame(currentMonth, "month")
        const isPast = day.isBefore(minDate, "day")

        days.push(
          <div
            key={day.format("YYYY-MM-DD")}
            className={`
              py-2 text-center cursor-pointer select-none
              ${isCurrentMonth ? "text-white" : "text-white/30"}
              ${isPast && isCurrentMonth ? "text-white/30 cursor-not-allowed" : ""}
              ${isSelected && isCurrentMonth ? "bg-[#0A84FF]/20 !text-[#0A84FF] rounded-full" : ""}
            `}
            onClick={() =>
              !isPast && isCurrentMonth && handleDateClick(cloneDay)
            }
          >
            <span className="text-lg">{formattedDate}</span>
          </div>,
        )
        day = moment(day).add(1, "day")
      }
      rows.push(
        <div key={day.format("YYYY-MM-W")} className="grid grid-cols-7 gap-y-2">
          {days}
        </div>,
      )
      days = []
    }

    return <div className="mt-2">{rows}</div>
  }

  // Function to handle date click
  const handleDateClick = (day: moment.Moment) => {
    const newDay = moment(day.format("MMMM DD YYYY"), ["MMMM DD YYYY"])

    if (dateTimeState.activeField === "startDate") {
      const end = moment(`${dateTimeState.endDate} ${dateTimeState.endTime}`, [
        "MMMM DD YYYY hh:mm A",
      ])
      const start = moment(
        `${newDay.format("MMMM DD YYYY")} ${dateTimeState.startTime}`,
        ["MMMM DD YYYY hh:mm A"],
      )

      // The DateTimePicker component will handle the auto-adjustment logic
      // We just need to pass the date change and let it decide
      onChange(day)
    } else if (dateTimeState.activeField === "endDate") {
      if (allowSelectingPastDates) {
        onChange(day)
        return
      }
      const start = moment(
        `${dateTimeState.startDate} ${dateTimeState.startTime}`,
        ["MMMM DD YYYY hh:mm A"],
      )
      const end = moment(
        `${newDay.format("MMMM DD YYYY")} ${dateTimeState.endTime}`,
        ["MMMM DD YYYY hh:mm A"],
      )

      if (!end.isAfter(start)) {
        showErrorToast("End date and time must be after start date and time")
      } else {
        onChange(day)
      }
    }
  }

  // Function to move to previous month
  const prevMonth = () => {
    setCurrentMonth(moment(currentMonth).subtract(1, "month"))
  }

  // Function to move to next month
  const nextMonth = () => {
    setCurrentMonth(moment(currentMonth).add(1, "month"))
  }

  return (
    <div className="p-2">
      {renderHeader()}
      {renderDays()}
      {renderCells()}
    </div>
  )
}

export default Calendar
